import { useMemo, useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import { Currency } from 'common/types/format';
import { ValueViewerProps } from 'common/types/scalarSpreadsheet';
import CurrencyMenu from 'components/CurrencyMenu';
import { StyledCurrencyButton } from 'components/Grid/GridFormatter/GridFormatter.styles';
import { useCurrencies } from 'components/Grid/GridFormatter/useCurrencies';
import { useLayoutContext } from 'context/LayoutContext';
import { useWaterfallFXContext } from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/WaterfallFXContext/WaterfallFXContext';
import { WaterfallFXRowLabelProps } from './WaterfallFXRowLabel.props';

export const WaterfallFXRowLabel = ({ showStandardCurrency }: WaterfallFXRowLabelProps) => {
  const context = useWaterfallFXContext();
  const { isExportMode } = useLayoutContext();
  const { currencies, showLess, showMore, usedCurrencies } = useCurrencies();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const searchableCurrencies = useMemo(() => (
    context?.currencyList.map(currency => ({
      code: currency.sourceCurrency.toString(),
      symbol: currency.symbol,
    })) ?? []
  ), [context?.currencyList]);

  if (context) {
    const { currencyList, isLoading, selectedCurrency, setSelectedCurrency, standardCurrency } = context;
    const displayedCurrency = showStandardCurrency
      ? (standardCurrency ?? selectedCurrency?.sourceCurrency)
      : selectedCurrency?.sourceCurrency;
    const isForeignCurrency = !showStandardCurrency && displayedCurrency !== standardCurrency;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleSelect = (currency: Currency) => {
      const additionalCurrency = currencyList.find(item => item.sourceCurrency === currency.code);

      if (additionalCurrency) {
        setSelectedCurrency(additionalCurrency);
      }
      handleClose();
    };

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
          <StyledCurrencyButton
            $foreignCurrency={isForeignCurrency}
            $isExportMode={isExportMode}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu"
            endIcon={<ExpandMore />}
            onClick={handleClick}
            sx={theme => ({
              borderRadius: '10px',
              marginRight: theme.spacing(2.5),
              maxWidth: '100%',
              padding: '0 6px 0 8px',
              transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
                duration: theme.transitions.duration.shorter,
              }),
              '& .MuiButton-endIcon': {
                marginLeft: theme.spacing(0.25),
                marginRight: 0,
              },
              '& .MuiButton-endIcon svg': {
                color: 'inherit',
                fill: 'currentColor !important',
                fontSize: '1rem',
              },
              '&:hover': {
                backgroundColor: 'grey.100',
                borderColor: 'grey.400',
                color: 'grey.800',
              },
            })}>
            {displayedCurrency}
          </StyledCurrencyButton>
        </div>
        <CurrencyMenu
          anchorEl={anchorEl}
          currencies={currencies}
          data-testid={showStandardCurrency
            ? 'waterfall-base-currency-menu'
            : 'waterfall-foreign-currency-menu'}
          isCurrencyLoading={isLoading}
          onClose={handleClose}
          onCurrencyChange={handleSelect}
          searchableCurrencies={searchableCurrencies}
          showLess={showLess}
          showMore={showMore}
          usedCurrencies={usedCurrencies}
        />
      </>
    );
  }

  return null;
};

// Wrapper that forces display of the company's standard currency on the chip
export const StandardExchangeRateRowLabel = (props: ValueViewerProps) => (
  <WaterfallFXRowLabel {...props} showStandardCurrency />
);
